/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useEffect, useState } from "react";

import AppBar from "@mui/material/AppBar";
// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
// Images
import MrCrypto from "assets/MrCryptoImages/WhiteHat.png";
// Vision UI Dashboard React base styles
import breakpoints from "assets/theme/base/breakpoints";
import VuiAvatar from "components/VuiAvatar";
// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
// Vision UI Dashboard React icons
import { IoCube } from "react-icons/io5";
import { IoDocument } from "react-icons/io5";
import { IoBuild } from "react-icons/io5";
// Vision UI Dashboard React example components
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";

// Moralis
import { useMoralisWeb3Api, useMoralis } from "react-moralis";
import { tokenValue } from "helpers/formatters";

function Header() {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.lg
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  // Moralis
  const Web3Api = useMoralisWeb3Api();
  const { isInitialized, isAuthenticated, account } = useMoralis();
  const [balances, setBalances] = useState([]);

  useEffect(() => {
    if (isInitialized && isAuthenticated && account) {
      fetchTokenBalances();
    }
  }, []);

  const fetchTokenBalances = async (token) => {
    const options = {
      chain: token,
    };
    const balances = await Web3Api.account.getTokenBalances(options);
    setBalances(balances);
  };

  const columns = [
    { name: "name", align: "center" },
    { name: "symbol", align: "center" },
    { name: "balance", align: "center" },
    { name: "address", align: "center" },
  ];

  const rows = () => {
    return balances.map((elem) => ({
      name: (
        <VuiTypography variant="button" color="white">
          {elem.name}
        </VuiTypography>
      ),
      symbol: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {elem.symbol}
        </VuiTypography>
      ),
      balance: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {tokenValue(elem.balance, elem.decimals)}
        </VuiTypography>
      ),
      address: (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {elem.token_address}
        </VuiTypography>
      ),
    }));
  };

  return (
    <VuiBox position="relative">
      <DashboardNavbar light />
      <Card
        sx={{
          px: 3,
          mt: 2,
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          sx={({ breakpoints }) => ({
            [breakpoints.up("xs")]: {
              gap: "16px",
            },
            [breakpoints.up("xs")]: {
              gap: "0px",
            },
            [breakpoints.up("xl")]: {
              gap: "0px",
            },
          })}
        >
          <Grid
            item
            xs={12}
            md={1.7}
            lg={1.5}
            xl={1.2}
            xxl={0.8}
            display="flex"
            sx={({ breakpoints }) => ({
              [breakpoints.only("sm")]: {
                justifyContent: "center",
                alignItems: "center",
              },
            })}
          >
            <VuiAvatar src={MrCrypto} alt="profile-image" variant="rounded" size="xl" shadow="sm" />
          </Grid>
          <Grid item xs={12} md={4.3} lg={4} xl={3.8} xxl={7}>
            <VuiBox
              height="100%"
              mt={0.5}
              lineHeight={1}
              display="flex"
              flexDirection="column"
              sx={({ breakpoints }) => ({
                [breakpoints.only("sm")]: {
                  justifyContent: "center",
                  alignItems: "center",
                },
              })}
            >
              <VuiTypography variant="lg" color="white" fontWeight="bold">
                Mr Crypto
              </VuiTypography>
              <VuiTypography variant="button" color="text" fontWeight="regular">
                mrcrypto@crypto.com
              </VuiTypography>
            </VuiBox>
          </Grid>
          <Grid item xs={12} md={6} lg={6.5} xl={6} xxl={4} sx={{ ml: "auto" }}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue}
                onChange={handleSetTabValue}
                sx={{ background: "transparent", display: "flex", justifyContent: "flex-end" }}
              >
                <Tab
                  onClick={() => fetchTokenBalances()}
                  label="ETH"
                  icon={<IoCube color="white" size="16px" />}
                />
                <Tab
                  onClick={() => fetchTokenBalances("bsc")}
                  label="BSC"
                  icon={<IoDocument color="white" size="16px" />}
                />
                <Tab
                  onClick={() => fetchTokenBalances("matic")}
                  label="MATIC"
                  icon={<IoBuild color="white" size="16px" />}
                />
              </Tabs>
            </AppBar>
          </Grid>
        </Grid>
      </Card>

      <Card
        sx={{
          px: 3,
          mt: 2,
        }}
      >
        <Grid>
          <VuiBox
            sx={{
              "& th": {
                borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                  `${borderWidth[1]} solid ${grey[700]}`,
              },
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
              },
            }}
          >
            <Table columns={columns} rows={rows()} />
          </VuiBox>
        </Grid>
      </Card>
    </VuiBox>
  );
}

export default Header;
